import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, CheckCircle2, XCircle, Loader2, Bot } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * MetaAutoresponderCard (iter152) — admin dashboard control panel for the
 * Facebook + Instagram Reel comment → auto-DM auto-responder.
 *
 * Shows setup status so the operator can see exactly which env vars are
 * still missing (green tick vs red cross), and a dry-run tester so they
 * can validate the keyword matcher WITHOUT posting a real comment.
 */
export default function MetaAutoresponderCard() {
  const [status, setStatus] = useState(null);
  const [dryText, setDryText] = useState("Comment APP for the link!");
  const [dryResult, setDryResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/meta-autoresponder/status");
      setStatus(data);
    } catch { /* silent */ }
  };

  useEffect(() => { loadStatus(); }, []);

  const runDry = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/meta-autoresponder/dry-run", { text: dryText });
      setDryResult(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  const Row = ({ label, ok, hint }) => (
    <div className="flex items-center justify-between py-1.5 text-xs">
      <span className="text-[#444]">{label}</span>
      <span className={`inline-flex items-center gap-1 font-semibold ${ok ? "text-[#065f46]" : "text-[#b91c1c]"}`}>
        {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
        {ok ? "set" : (hint || "missing")}
      </span>
    </div>
  );

  return (
    <section
      className="border hairline rounded-2xl p-5 sm:p-6 mb-6 bg-white"
      data-testid="admin-meta-autoresponder"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-1">
            <Bot className="w-3 h-3" /> Reel automation
          </div>
          <h2 className="font-display text-xl sm:text-2xl tracking-tight">
            Comment-to-DM auto-responder
          </h2>
          <p className="text-xs text-[#666] mt-1 max-w-xl">
            When someone comments <strong>{status?.trigger_keyword || "APP"}</strong> on any of your Facebook or Instagram Reels, we reply publicly and DM them the app link. Uses Meta's private-reply window (7 days per comment).
          </p>
        </div>
        {status?.configured ? (
          <span className="text-[11px] px-2 py-1 rounded-full bg-[#ecfdf5] text-[#065f46] font-semibold flex items-center gap-1 shrink-0">
            <CheckCircle2 className="w-3 h-3" /> Live
          </span>
        ) : (
          <span className="text-[11px] px-2 py-1 rounded-full bg-[#fef3c7] text-[#92400e] font-semibold shrink-0">
            Setup incomplete
          </span>
        )}
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <div className="border hairline rounded-lg p-3 bg-[#fafafa]">
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#666] mb-2">
            Environment secrets
          </div>
          <Row label="META_APP_SECRET" ok={status?.app_secret_set} />
          <Row label="META_VERIFY_TOKEN" ok={status?.verify_token_set} />
          <Row label="META_PAGE_ACCESS_TOKEN" ok={status?.page_token_set} />
          <Row label="META_PAGE_ID" ok={status?.page_id_set} />
          <Row label="META_IG_ID" ok={status?.ig_id_set} hint="optional if FB-only" />
        </div>
        <div className="border hairline rounded-lg p-3 bg-[#fafafa]">
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#666] mb-2">
            Activity
          </div>
          <div className="text-xs text-[#444] space-y-1">
            <div>Comments processed: <strong>{status?.counts?.total ?? "—"}</strong></div>
            <div>DMs sent: <strong className="text-[#065f46]">{status?.counts?.sent ?? "—"}</strong></div>
            <div>Errors: <strong className={status?.counts?.errors ? "text-[#b91c1c]" : ""}>{status?.counts?.errors ?? "—"}</strong></div>
          </div>
        </div>
      </div>

      <div className="mt-4 border hairline rounded-lg p-3">
        <div className="text-[11px] uppercase tracking-wider font-bold text-[#666] mb-2">
          Test keyword matcher
        </div>
        <div className="flex gap-2 items-center">
          <input
            value={dryText}
            onChange={(e) => setDryText(e.target.value)}
            placeholder="Paste a sample comment…"
            className="flex-1 px-3 py-2 border hairline rounded-lg text-sm"
            data-testid="meta-dry-run-input"
          />
          <button
            type="button"
            disabled={busy}
            onClick={runDry}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#ff4500] text-white text-sm font-semibold hover:bg-[#e63e00] disabled:opacity-60"
            data-testid="meta-dry-run-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
            Test
          </button>
        </div>
        {dryResult && (
          <div className={`mt-2 text-xs font-semibold ${dryResult.matches ? "text-[#065f46]" : "text-[#666]"}`}>
            {dryResult.matches ? `✓ Would trigger DM (keyword "${dryResult.keyword}" found)` : `✗ No match — keyword "${dryResult.keyword}" not found`}
          </div>
        )}
      </div>

      {!status?.configured && (
        <div className="mt-4 border-l-4 border-[#ff4500] bg-[#fff4ee] p-3 text-xs text-[#7c2d12] rounded-r">
          <strong>Setup required.</strong> Ask Anthony (E1) to run you through the 4-step Meta App Dashboard setup and paste the 5 env vars into your production secrets. Nothing goes live until all rows above show green.
        </div>
      )}
    </section>
  );
}
